import type { EnvironmentConfig } from '@luna/env';
import { getEnv } from '@luna/env';
import type { TransactionsSort } from '@shared/transactions-types';
import type { SearchParams, SearchState } from '@shared/transactions-search-state';
import { transformSearchStateToQueryParams } from '@shared/transactions-search-state';
import { createQueryString } from '@luna/router';
import { isFeatureEnabled } from '@luna/gatekeeper';

import { getTransactionsFeaturesParams } from './getTransactionsFeaturesParams';

export interface TransactionsUrlArgs {
    searchState?: SearchState;
    sort?: TransactionsSort;
    productId?: string;
    page?: number;
    pageSize?: number;
    isWithSum?: boolean;
    isWithSumWithRoundup?: boolean;
    isWithCount?: boolean;
    isWithBalance?: boolean;
    isWithProducts?: boolean;
}

export interface TransactionUrlArgs {
    transactionId: string;
    productId?: string;
}

export function getURLSearchParams(params: { [key: string]: string | string[] }): string {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, values]) => {
        if (Array.isArray(values)) {
            values.forEach((value) => {
                searchParams.append(key, value);
            });
        } else {
            searchParams.append(key, values);
        }
    });

    return searchParams.toString();
}

export function getTransactionsUrl({
    searchState,
    sort,
    productId,
    page,
    pageSize,
    isWithSum,
    isWithSumWithRoundup,
    isWithCount,
    isWithBalance,
    isWithProducts,
}: TransactionsUrlArgs): string {
    const featuresParams = getTransactionsFeaturesParams();
    const searchParams = composeSearchParams(searchState);

    const includeContainedTransactions = String(
        shouldIncludeContainedTransactions(searchParams, searchState),
    );

    const queryParams = getURLSearchParams({
        ...featuresParams,
        ...searchParams,
        ...(sort && { sort }),
        ...(productId && { id: productId }),
        includeContainedTransactions,
        page: String(page),
        pageSize: String(pageSize),
        ...getQueryFlags({
            sum: isWithSum,
            sumWithRoundup: isWithSumWithRoundup,
            count: isWithCount,
            balance: isWithBalance,
            includeProducts: isWithProducts,
        }),
    });

    const { georgeApiUrl } = getEnv<EnvironmentConfig>();

    return `${georgeApiUrl}my/transactions?${queryParams}`;
}

export function getTransactionUrl({ productId, transactionId }: TransactionUrlArgs): string {
    const includeContainedTransactions = String(true);
    const featuresParams = getTransactionsFeaturesParams();

    const queryParams = createQueryString({
        ...featuresParams,
        ...(productId && { id: productId }),
        transactionId,
        includeContainedTransactions,
    });

    const { georgeApiUrl } = getEnv<EnvironmentConfig>();

    return `${georgeApiUrl}my/transactions${queryParams}`;
}

export function composeSearchParams(state?: SearchState): SearchParams {
    return getDefinedEntries(transformSearchStateToQueryParams(state));
}

export function shouldIncludeContainedTransactions(
    params: SearchParams,
    state?: SearchState,
): boolean {
    if (isFeatureEnabled((f) => f.transactions.replaceBulkTransactions)) {
        return false;
    }
    if (state?.includeContainedTransactions === 'false') {
        return false;
    }
    if (state?.includeContainedTransactions === 'true') {
        return true;
    }
    if (Object.keys(params).length === 0) {
        return false;
    }
    return !hasOnlyDirectionParam(params);
}

export function getQueryFlags(
    booleanFlags: Record<string, boolean | undefined>,
): Record<string, string> {
    const keys = Object.keys(booleanFlags);
    const queryFlags: Record<string, string> = {};
    keys.forEach((key) => {
        if (booleanFlags[key] !== undefined) {
            queryFlags[key] = String(booleanFlags[key]);
        }
    });
    return queryFlags;
}

function hasOnlyDirectionParam(params: SearchParams): boolean {
    const param: keyof SearchParams = 'direction';
    const hasDirectionParam = param in params;
    const hasOnlyOneParam = Object.keys(params).length === 1;
    return hasDirectionParam && hasOnlyOneParam;
}

function getDefinedEntries(params: SearchParams): SearchParams {
    return Object.entries(params).reduce((accumulator, [key, value]) => {
        const isDefined = Array.isArray(value) ? value.length > 0 : Boolean(value);
        return isDefined ? { ...accumulator, [key]: value } : accumulator;
    }, {});
}
